import { initEccLib } from "rujira.js";
import * as ecc from "tiny-secp256k1";
import { configureRelayUtxoFetcher } from "./services/utxo";
// import "./services/sentry";
initEccLib(ecc);
configureRelayUtxoFetcher();

import clsx from "clsx";
import { MotionConfig } from "motion/react";
import { Component, FC, PropsWithChildren, StrictMode, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ReactDOM from "react-dom/client";
import { Toaster } from "react-hot-toast";
import { BrowserRouter, Navigate, Routes, useLocation } from "react-router-dom";
import {
  Footer,
  GlobalModalOutlet,
  GlobalTooltip,
  I18nProvider,
  LoaderWithMessages,
  TxButtonTip,
  useLocale,
} from "rujira.ui";
import "rujira.ui/scss/index.scss";
import { Borrow } from "./borrow/Borrow";
import { BalanceSubscriptionProvider } from "./common/components/Balance";
import { ApplicationBoundary } from "./common/components/ApplicationBoundary";
import { Header } from "./common/components/Header";
import { PrivateSessionExpiry } from "./common/components/PrivateSessionExpiry";
import { PrivateBackupReminder } from "./common/components/PrivateBackupReminder";
import ScrollToTop from "./common/components/ScrollToTop";
import { NotFound } from "./common/NotFound";
import { PrivacyPolicy } from "./common/PrivacyPolicy";
import { TermsOfUse } from "./common/TermsOfUse";
import { VultisigProvider } from "./common/Vultisig";
import { ContextWrapper } from "./ContextWrapper";
import { ModelBScope } from "./redacted/ModelBScope";
import { AnalyticsSample } from "./developer/AnalyticsSample.tsx";
import { ContractPage } from "./developer/Contract";
import { Deployment } from "./developer/Deployment";
import { Link, route } from "./Gate";
import { Home } from "./home/Home";
import "./index.scss";
import { Index } from "./index/Index";
import { Indexes } from "./index/Indexes";
import { Leaderboard } from "./leagues/Leaderboard";
import { Merge } from "./merge/Merge";
import { Connect } from "./portfolio/components/Connect";
import { Portfolio } from "./portfolio/Portfolio";
import { AccountDataContext } from "./services/accountData";
import { AccountsContext } from "./services/accounts";
import { AccountScopedModalProvider } from "./services/accountScope";
import {
  PendingDepositLoadedContext,
  PendingDepositStorageContext,
} from "./services/deposits";
import { FavoritesContext } from "./services/favorites";
import { NotificationContext } from "./services/notifcation";
import { RelayContext } from "./services/relay";
import { RedactedContext } from "./services/redacted";
import { RedactedV2Context } from "./redacted/v2";
import { Lend } from "./lending/Lend";
import { Algorithmic } from "./algorithmic/Algorithmic";
import { Lending } from "./lending/Lending";
import { Liquidate } from "./liquidate/Liquidate";
import { Stake } from "./staking/Stake";
import { StakeOverview } from "./staking/StakeOverview";
import { Strategies } from "./strategies/Strategies";
import { Strategy } from "./strategies/Strategy";
import { Swap } from "./swap/Swap";
import { TorChart } from "./tor/TorChart";
import { Trade } from "./trade/Trade";
import { TradeMarkets } from "./trade/TradeMarkets";

const TOAST_OPTIONS = {
  style: {
    background: "#22242f",
    color: "#fff",
    fontSize: 14,
    fontWeight: 500,
  },
  success: {
    style: {
      background: "#34aa89",
      color: "#fff",
    },
    iconTheme: {
      primary: "#fff",
      secondary: "#34aa89",
    },
  },
  error: {
    style: {
      background: "#B71C1C",
      color: "#fff",
    },
    iconTheme: {
      primary: "#fff",
      secondary: "#B71C1C",
    },
  },
};

const Wrapper: FC<PropsWithChildren> = ({ children }) => {
  const location = useLocation();
  const { localePrefix } = useLocale();
  const rootPath = localePrefix
    ? location.pathname.substring(localePrefix.length) || "/"
    : location.pathname;
  const normalizedRootPath = rootPath.startsWith("/")
    ? rootPath
    : `/${rootPath}`;

  const routes = import.meta.env.VITE_STATIC;
  const STATIC: string[] = routes ? routes.split(",") : [];

  return (
    <div
      className={clsx({
        rujira: true,
        "rujira--dark":
          normalizedRootPath === "/" ||
          normalizedRootPath === "/leaderboard" ||
          normalizedRootPath === "/developer/canvas-test" ||
          normalizedRootPath.startsWith("/merge") ||
          normalizedRootPath.startsWith("/switch") ||
          normalizedRootPath.startsWith("/ecosystem"),
      })}>
      {children}
      <Suspense fallback={null}>
        <Footer routerElement={Link} staticRoutes={STATIC} />
      </Suspense>
      <Suspense fallback={null}>
        <TxButtonTip />
      </Suspense>
    </div>
  );
};

const Pages: FC = () => {
  return (
    <Suspense fallback={<LoaderWithMessages />}>
      <Routes>
        {route({ index: true, element: <Home /> })}
        {(["deposit", "withdraw", "send"] as const).map((action) =>
          route({
            path: action,
            element: <PortfolioActionRedirect action={action} />,
          })
        )}
        {route({ path: "connect", element: <Connect /> })}
        {route({ path: "merge/:asset?", element: <Merge /> })}
        {route({ path: "portfolio/*", element: <Portfolio /> })}
        {route({ path: "strategies", element: <Strategies /> })}
        {route({
          path: "strategies/:category/:type/:assets",
          element: <Strategy />,
        })}
        {route({ path: "strategies/:category/:assets", element: <Strategy /> })}
        {route({ path: "lend", element: <Lending /> })}
        {route({
          path: "automated",
          element: <Navigate to="/automated-trading" replace />,
        })}
        {route({ path: "automated-trading", element: <Algorithmic /> })}
        {route({ path: "lend/:asset", element: <Lend /> })}
        {route({ path: "stake", element: <StakeOverview /> })}
        {route({ path: "stake/:asset", element: <Stake /> })}
        {route({ path: "liquidate", element: <Liquidate /> })}
        {route({ path: "index", element: <Indexes /> })}
        {route({ path: "index/:asset", element: <Index /> })}
        {route({
          path: "swap",
          element: <Navigate to="/swap/ETH/BTC" replace />,
        })}
        {route({ path: "swap/:from?/:to?", element: <Swap /> })}
        {route({ path: "trade", element: <TradeMarkets /> })}
        {route({ path: "trade/:base/:quote", element: <Trade /> })}
        {route({
          path: "borrow",
          element: <Navigate to="/borrow/BTC/USDC" replace />,
        })}
        {route({ path: "borrow/:collateral", element: <Borrow /> })}
        {route({ path: "borrow/:collateral/:debt", element: <Borrow /> })}
        {route({ path: "thorchain/:asset/TOR", element: <TorChart /> })}
        {route({ path: "leaderboard", element: <Leaderboard /> })}
        {route({ path: "privacypolicy", element: <PrivacyPolicy /> })}
        {route({ path: "tou", element: <TermsOfUse /> })}
        {route({ path: "developer/deployment", element: <Deployment /> })}
        {route({
          path: "developer/analytics-test",
          element: <AnalyticsSample />,
        })}
        {route({
          path: "developer/contract/:address",
          element: <ContractPage />,
        })}
        {route({ path: "*", element: <NotFound /> })}
      </Routes>
    </Suspense>
  );
};

const PortfolioActionRedirect: FC<{
  action: "deposit" | "withdraw" | "send";
}> = ({ action }) => {
  const { toRoot } = useLocale();
  return <Navigate to={toRoot(`portfolio/${action}`)} replace />;
};

type State = { error: Error | null | undefined };

class ErrorBoundary extends Component<PropsWithChildren, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  render() {
    const { children } = this.props;
    const { error } = this.state;
    if (error) {
      return (
        <>
          <Header />
          <div className="rujira__main rujira__main--gradient">
            <div className="rujira__inner rujira__inner--pad">
              <section className="home__banner">
                <p>{error instanceof Error ? error.message : String(error)}</p>
              </section>
            </div>
          </div>
        </>
      );
    }
    return children;
  }
}

// Session services must be able to request consent, while modal contents still
// inherit Relay, account data, balances and the other application contexts.
const ApplicationModalOutlet: FC<PropsWithChildren> = ({ children }) => (
  <>
    {children}
    <PrivateSessionExpiry />
    <PrivateBackupReminder />
    <Suspense fallback={null}>
      <GlobalModalOutlet />
    </Suspense>
  </>
);

const queryClient = new QueryClient();
ReactDOM.createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <MotionConfig reducedMotion="user">
      <QueryClientProvider client={queryClient}>
        <ApplicationBoundary>
          <ContextWrapper
            contexts={[
              BrowserRouter,
              I18nProvider,
              RelayContext,
              RedactedContext,
              AccountsContext,
              AccountScopedModalProvider,
              RedactedV2Context,
              NotificationContext,
              FavoritesContext,
              AccountDataContext,
              PendingDepositStorageContext,
              PendingDepositLoadedContext,
              VultisigProvider,
              BalanceSubscriptionProvider,
              ApplicationModalOutlet,
            ]}>
            <Suspense fallback={<LoaderWithMessages />}>
              <Wrapper>
                <ErrorBoundary>
                  <Header />
                  <ModelBScope />
                  <Pages />
                  <ScrollToTop />
                </ErrorBoundary>
              </Wrapper>
              <Suspense fallback={null}>
                <GlobalTooltip />
              </Suspense>
              <Suspense fallback={null}>
                <Toaster toastOptions={TOAST_OPTIONS} />
              </Suspense>
            </Suspense>
          </ContextWrapper>
        </ApplicationBoundary>
      </QueryClientProvider>
    </MotionConfig>
  </StrictMode>
);
